import { ApolloClient, gql } from 'apollo-boost';
import { ReportStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import { InvestmentReportDates } from 'shared/apollo/fragments/allocation';

const ALLOCATION_FIELDS = `
  reportId
  report {
    dbId
    year
    status
    endDate
    enabled
    programId
    version
    program {
      id
      name
      managerId
      visibility
      manager {
        firstName
        lastName
        email
      }
    }
  }
  investment {
    dbId
    donation
    totalAllocated
    fullyAllocated
    unallocatedAmount
    allocationState
    tags
    programNameByDonor
    reportDate1
    reportDate2
    reportDate3
    reportDate4
    reportDate5
    reportDate6
    reportDate7
    reportDate8
    portfolio {
      year
      isImpactSnapshot
      funder {
        name
      }
      relationshipManager {
        firstName
        lastName
        email
      }
    }
  }
`;

const INVITATION_FIELDS = `
  firstName
  lastName
  email
  createdAt
  investment {
    dbId
    donation
    totalAllocated
    fullyAllocated
    unallocatedAmount
    allocationState
    status
    programNameByDonor
    reportDate1
    reportDate2
    reportDate3
    reportDate4
    reportDate5
    reportDate6
    reportDate7
    reportDate8
    allocations {
      edges {
        node {
          reportId
          report {
            endDate
            programId
            program {
              name
              managerId
              visibility
              manager {
                firstName
                lastName
                email
              }
            }
          }
        }
      }
    }
    portfolio {
      year
      name
      isImpactSnapshot
      relationshipManager {
        email
        firstName
        lastName
      }
      funder {
        name
      }
    }
  }
`;

const unallocatedInvestmentsQuery = gql`
  query unallocatedInvestments {
    unallocatedInvestments {
      dbId
      donation
      totalAllocated
      unallocatedAmount
      allocationState
      hasTask
      dueDate
      tasks {
        dbId
        taskType
        dueDate
        status
        completionDate
        overdueDays
        invitationId
        allocationId
      }
      allocations {
        ${ALLOCATION_FIELDS}
      }
      invitation {
        ${INVITATION_FIELDS}
      }
    }
  }
`;

const query = gql`
  query {
    allTasks {
      edges {
        node {
          taskId
          taskType
          dueDate
          status
          completionDate
          completionUser {
            firstName
            lastName
          }
          allocation {
            ${ALLOCATION_FIELDS}
          }
          invitation {
            ${INVITATION_FIELDS}
          }
        }
      }
    }
  }
`;

interface Allocation {
  reportId: number;
  report: {
    endDate: string; // '2024-08-31'
    programId: number;
    program: {
      name: string;
      managerId: number;
      manager: {
        email: string;
        firstName: string;
        lastName: string;
      };
    };
  };
}

export type AllocationState =
  | 'UNALLOCATED'
  | 'PARTIALLY_ALLOCATED'
  | 'FULLY_ALLOCATED';

export type InvestmentStatus =
  | 'UNNOTIFIED'
  | 'SENDING'
  | 'WAITING'
  | 'PARTIALLY_ALLOCATED'
  | 'ALLOCATED'
  | 'RETURNED'
  | 'REJECTED'
  | 'APPLICATION'
  | 'SCHEDULED';

interface Investment extends InvestmentReportDates {
  dbId: number;
  donation: number;
  totalAllocated?: number;
  fullyAllocated?: boolean;
  // Server-owned remainder. Never recompute it from donation - totalAllocated.
  unallocatedAmount?: number;
  allocationState?: AllocationState;
  tags: string[] | null;
  // version?: 'v4' | 'v3' | null;
  portfolio: {
    name: string;
    year: string;
    isImpactSnapshot?: boolean;
    funder: { name: string };
    relationshipManager?: {
      firstName: string;
      lastName: string;
      email: string;
    } | null;
  };
  programNameByDonor: string;
}

export interface Report {
  dbId: number;
  year: string;
  status: ReportStatus;
  enabled?: boolean;
  version: 'v4' | 'v3' | null;
  programId: number;
  endDate: string; // '2022-08-17'
  program: {
    id: string; // 'UHJvZ3JhbToxNjk='
    name: string;
    managerId: number | null;
    manager: {
      email: string;
      firstName: string;
      lastName: string;
    } | null;
  } | null;
}

export interface Task {
  taskId: number;
  allocation: {
    reportId: number;
    investment: Investment;
    report: Report;
  } | null;
  completionDate: string | null; // '2023-05-25'
  completionUser: { firstName: string; lastName: string } | null;
  dueDate: string; // '2022-09-19'
  status: 'OVERDUE' | 'COMPLETED' | 'UNCOMPLETED';
  taskType:
    | 'ALLOCATE'
    | 'SUBMIT'
    | 'UPDATE'
    | 'RESOLVE'
    | 'PUBLISH'
    | 'FINALIZE'
    | 'INTERIM_UPDATE';
  invitation: null | {
    firstName: string;
    lastName: string;
    email: string;
    createdAt: string;
    investment:
      | (Investment & {
          allocations: Allocation[];
          status?: InvestmentStatus;
        })
      | null;
  };
}

export const getTasksQuery = (client: ApolloClient<object>) => {
  return client.query({
    query,
    fetchPolicy: 'network-only',
  });
};

export interface UnallocatedInvestment {
  dbId: number;
  donation: number;
  totalAllocated: number;
  unallocatedAmount: number;
  allocationState: AllocationState;
  hasTask: boolean;
  dueDate: string; // '2022-09-19'
  tasks: {
    dbId: number;
    taskType: Task['taskType'];
    dueDate: Task['dueDate'];
    status: Task['status'];
    completionDate: Task['completionDate'];
    overdueDays: number;
    invitationId: number;
    allocationId: number;
  }[];
  allocations: Task['allocation'][];
  invitation: Task['invitation'];
}

export const getUnallocatedInvestmentsQuery = (
  client: ApolloClient<object>,
) => {
  return client.query<{ unallocatedInvestments: UnallocatedInvestment[] }>({
    query: unallocatedInvestmentsQuery,
    fetchPolicy: 'network-only',
  });
};

const updateTaskMutation = gql`
  mutation updateTaskMutation($id: Int!, $status: String!) {
    updateTask(id: $id, status: $status) {
      success
      id
    }
  }
`;

export const updateTask = (
  client: ApolloClient<object>,
  variables: { id: Task['taskId']; status: Task['status'] },
) => {
  const status = variables.status.toLowerCase();
  return client.mutate({
    mutation: updateTaskMutation,
    variables: { ...variables, status },
    fetchPolicy: 'no-cache',
  });
};

const deleteInvestmentMutation = gql`
  mutation deleteInvestment($id: Int!) {
    deleteInvestment(id: $id) {
      success
    }
  }
`;

export const deleteInvestment = (
  client: ApolloClient<object>,
  variables: { id: number },
) => {
  return client.mutate<{
    deleteInvestment: { success: boolean };
  }>({
    mutation: deleteInvestmentMutation,
    variables,
    fetchPolicy: 'no-cache',
  });
};

const invitationByTokenQuery = gql`
  query invitationByToken($token: String!) {
    invitationByToken(token: $token) {
      dbId
      firstName
      lastName
      email
      createdAt
      claimed
      claimedByCurrentUser
      investment {
        dbId
        donation
        totalAllocated
        fullyAllocated
        status
        programNameByDonor
        reportDate1
        reportDate2
        reportDate3
        reportDate4
        reportDate5
        reportDate6
        reportDate7
        reportDate8
        portfolio {
          year
          name
          isImpactSnapshot
          funder {
            name
          }
          relationshipManager {
            email
            firstName
            lastName
          }
        }
        allocations {
          edges {
            node {
              reportId
              report {
                endDate
                programId
                program {
                  name
                  managerId
                  visibility
                  manager {
                    firstName
                    lastName
                    email
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export interface InvitationByTokenResponse {
  invitationByToken: {
    dbId: number;
    firstName: string;
    lastName: string;
    email: string;
    createdAt: string;
    claimed: boolean;
    claimedByCurrentUser: boolean;
    investment: {
      dbId: number;
      donation: number;
      totalAllocated?: number;
      fullyAllocated?: boolean;
      status: string;
      programNameByDonor: string;
      reportDate1?: string;
      reportDate2?: string;
      reportDate3?: string;
      reportDate4?: string;
      reportDate5?: string;
      reportDate6?: string;
      reportDate7?: string;
      reportDate8?: string;
      portfolio: {
        year: string;
        name: string;
        isImpactSnapshot?: boolean;
        funder: { name: string };
        relationshipManager?: {
          email: string;
          firstName: string;
          lastName: string;
        } | null;
      };
      allocations: {
        edges: {
          node: {
            reportId: number;
            report: {
              endDate: string;
              programId: number;
              program: {
                name: string;
                managerId: number;
                visibility: string;
                manager: {
                  firstName: string;
                  lastName: string;
                  email: string;
                } | null;
              };
            };
          };
        }[];
      };
    } | null;
  } | null;
}

export const getInvitationByToken = (
  client: ApolloClient<object>,
  token: string,
) => {
  return client.query<InvitationByTokenResponse>({
    query: invitationByTokenQuery,
    variables: { token },
    fetchPolicy: 'network-only',
  });
};

const claimInvitationMutation = gql`
  mutation claimInvitation($token: String!) {
    claimInvitation(token: $token) {
      success
      id
      investmentId
    }
  }
`;

export interface ClaimInvitationResponse {
  claimInvitation: {
    success: boolean;
    id: number;
    investmentId: number;
  };
}

// Claims the invitation for the current user: updates the invitation email,
// re-points the investment grantee, and ensures the allocate task exists.
export const claimInvitation = (
  client: ApolloClient<object>,
  token: string,
) => {
  return client.mutate<ClaimInvitationResponse>({
    mutation: claimInvitationMutation,
    variables: { token },
    fetchPolicy: 'no-cache',
  });
};
